import { keyframes } from "@emotion/react";

/**
 * Pulse animation - alternates opacity
 */
export const pulse = keyframes({
  "0%": { opacity: 1 },
  "50%": { opacity: 0.5 },
  "100%": { opacity: 1 },
});

/**
 * Rotate animation - full 360 degree rotation
 */
export const rotate = keyframes({
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
});

/**
 * FadeIn animation - transitions from invisible to visible
 */
export const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

/**
 * SlideUp animation - moves element up from below
 */
export const slideUp = keyframes({
  "0%": { transform: "translateY(25px)" },
  "100%": { transform: "translateY(0)" },
});

/**
 * SlideIn animation - slides element in from the right with fade
 */
export const slideIn = keyframes({
  "0%": {
    opacity: 0,
    transform: "translateX(10%)",
  },
  "100%": {
    opacity: 1,
    transform: "translateX(0)",
  },
});

const dotSizes = {
  2: {
    height: "2px",
    transform: "translateY(0)",
  },
  3: {
    height: "3px",
    transform: "translateY(-0.5px)",
  },
  4: {
    height: "4px",
    transform: "translateY(-1px)",
  },
  5: {
    height: "5px",
    transform: "translateY(-1.5px)",
  },
  6: {
    height: "6px",
    transform: "translateY(-2px)",
  },
  10: {
    height: "10px",
    transform: "translateY(-4px)",
  },
};

const voxDot1 = keyframes({
  "0%": dotSizes[2],
  "3%": dotSizes[2],
  "8%": dotSizes[5],
  "9%": dotSizes[5],
  "12.4%": dotSizes[3],
  "13.5%": dotSizes[4],
  "16%": dotSizes[2],
  "19%": dotSizes[2],
  "19.5%": dotSizes[4],
  "22%": dotSizes[4],
  "26.5%": dotSizes[2],
  "31.5%": dotSizes[3],
  "34.3%": dotSizes[2],
  "42%": dotSizes[5],
  "46.7%": dotSizes[3],
  "47.3%": dotSizes[4],
  "51%": dotSizes[4],
  "56.25%": dotSizes[2],
  "57.3%": dotSizes[2],
  "64%": dotSizes[5],
  "65.3%": dotSizes[5],
  "68.7%": dotSizes[3],
  "70%": dotSizes[4],
  "72.5%": dotSizes[2],
  "75.3%": dotSizes[2],
  "76%": dotSizes[4],
  "78%": dotSizes[4],
  "82.7%": dotSizes[2],
  "87.7": dotSizes[3],
  "90.5%": dotSizes[5],
  "95.4%": dotSizes[3],
  "97%": dotSizes[4],
  "100%": dotSizes[2],
});

const voxDot2 = keyframes({
  "0%": dotSizes[2],
  "1.5%": dotSizes[5],
  "3%": dotSizes[5],
  "4%": dotSizes[6],
  "6.8%": dotSizes[4],
  "7.2%": dotSizes[4],
  "8%": dotSizes[5],
  "8.5%": dotSizes[5],
  "10.5%": dotSizes[6],
  "15%": dotSizes[6],
  "20%": dotSizes[3],
  "20.5%": dotSizes[3],
  "21.5%": dotSizes[2],
  "25%": dotSizes[6],
  "26.5%": dotSizes[6],
  "29.8%": dotSizes[4],
  "32%": dotSizes[6],
  "37.8%": dotSizes[6],
  "45.3%": dotSizes[2],
  "48.5%": dotSizes[6],
  "50%": dotSizes[6],
  "53%": dotSizes[4],
  "57.8%": dotSizes[6],
  "61.3%": dotSizes[6],
  "67.8%": dotSizes[2],
  "71%": dotSizes[6],
  "72.5%": dotSizes[6],
  "79.3%": dotSizes[2],
  "83.5%": dotSizes[4],
  "86.3%": dotSizes[6],
  "87%": dotSizes[6],
  "90%": dotSizes[4],
  "92.5%": dotSizes[2],
  "95.3%": dotSizes[6],
  "95.8%": dotSizes[6],
  "100%": dotSizes[2],
});

const voxDot3 = keyframes({
  "0%": dotSizes[2],
  "1.5%": dotSizes[3],
  "3%": dotSizes[3],
  "7.3%": dotSizes[10],
  "8%": dotSizes[10],
  "12.3%": dotSizes[4],
  "15.5%": dotSizes[3],
  "20%": dotSizes[6],
  "21.5%": dotSizes[4],
  "23.8%": dotSizes[2],
  "31.8%": dotSizes[10],
  "34%": dotSizes[5],
  "35%": dotSizes[5],
  "38.3%": dotSizes[3],
  "39.3%": dotSizes[3],
  "42.3%": dotSizes[6],
  "43%": dotSizes[6],
  "46.8%": dotSizes[2],
  "47.8%": dotSizes[2],
  "50%": dotSizes[3],
  "54.5%": dotSizes[10],
  "60%": dotSizes[3],
  "62%": dotSizes[3],
  "65%": dotSizes[6],
  "68.8%": dotSizes[2],
  "69.5%": dotSizes[2],
  "71.8%": dotSizes[4],
  "72.5%": dotSizes[4],
  "77%": dotSizes[10],
  "81.5%": dotSizes[6],
  "88.8%": dotSizes[3],
  "93%": dotSizes[6],
  "96.8%": dotSizes[2],
  "98%": dotSizes[3],
  "100%": dotSizes[2],
});

const voxDot4 = keyframes({
  "0%": dotSizes[2],
  "1.5%": dotSizes[5],
  "3%": dotSizes[5],
  "4.5%": dotSizes[6],
  "7.3%": dotSizes[5],
  "9.8%": dotSizes[5],
  "11.3%": dotSizes[6],
  "13.3%": dotSizes[6],
  "17%": dotSizes[5],
  "19.3%": dotSizes[4],
  "20.8%": dotSizes[3],
  "21.5%": dotSizes[3],
  "23%": dotSizes[2],
  "26.8%": dotSizes[6],
  "30%": dotSizes[5],
  "32.3%": dotSizes[6],
  "34.8%": dotSizes[6],
  "37%": dotSizes[5],
  "38%": dotSizes[5],
  "45.5%": dotSizes[2],
  "50.3%": dotSizes[6],
  "52.5%": dotSizes[5],
  "53.8%": dotSizes[5],
  "56.5%": dotSizes[6],
  "58%": dotSizes[6],
  "59.3%": dotSizes[5],
  "62.5%": dotSizes[5],
  "64.8%": dotSizes[4],
  "66.3%": dotSizes[4],
  "68.5%": dotSizes[2],
  "69.3%": dotSizes[2],
  "72.3%": dotSizes[6],
  "73.8%": dotSizes[6],
  "76.8%": dotSizes[4],
  "79.8%": dotSizes[3],
  "84%": dotSizes[10],
  "87%": dotSizes[5],
  "89.8%": dotSizes[4],
  "93%": dotSizes[3],
  "94.5%": dotSizes[2],
  "97%": dotSizes[6],
  "100%": dotSizes[2],
});

const voxDot5 = keyframes({
  "0%": dotSizes[2],
  "1.5%": dotSizes[3],
  "6%": dotSizes[4],
  "8%": dotSizes[2],
  "10.5%": dotSizes[2],
  "13%": dotSizes[4],
  "16.3%": dotSizes[2],
  "20.8%": dotSizes[6],
  "22.3%": dotSizes[4],
  "23%": dotSizes[4],
  "24.5%": dotSizes[2],
  "26.5%": dotSizes[2],
  "27.8%": dotSizes[3],
  "32.5%": dotSizes[2],
  "34%": dotSizes[2],
  "34.8%": dotSizes[3],
  "37.8%": dotSizes[3],
  "40%": dotSizes[2],
  "41%": dotSizes[2],
  "44%": dotSizes[6],
  "44.8%": dotSizes[6],
  "48.5%": dotSizes[2],
  "53.3%": dotSizes[3],
  "54.8%": dotSizes[2],
  "57.8%": dotSizes[2],
  "59.5%": dotSizes[3],
  "61%": dotSizes[3],
  "62%": dotSizes[2],
  "63.5%": dotSizes[2],
  "66.3%": dotSizes[6],
  "70%": dotSizes[2],
  "73%": dotSizes[3],
  "73.8%": dotSizes[3],
  "77.3%": dotSizes[6],
  "81%": dotSizes[5],
  "85.3%": dotSizes[5],
  "88.3%": dotSizes[2],
  "92.5%": dotSizes[6],
  "96.5%": dotSizes[2],
  "98%": dotSizes[3],
  "100%": dotSizes[2],
});

export const voxAnimation = {
  voxDot1,
  voxDot2,
  voxDot3,
  voxDot4,
  voxDot5,
};

/**
 * Animations object for easy import
 */
export const animations = {
  pulse,
  rotate,
  fadeIn,
  slideUp,
  slideIn,
  voxAnimation,
};
